import { Box, Button, Typography } from '@mui/material';
import { ArrowUpwardSharp } from '@mui/icons-material';
import styled from 'styled-components';

import { ConfigDataType } from '@modules/ContactManager/components/Header/interfaces';
import { colorTheme } from '@styles/muiTheme';

export const HeaderWrapper = styled.div`
  position: sticky;
  top: 0;
  z-index: 3;

  display: flex;
  overflow: auto;
  min-height: 177px;
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.common.white};
  font-size: 14px;

  h2 {
    font-size: 20px;
  }

  svg {
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex: 2;
`;

export const NavButton = styled.div<{ disabled?: boolean }>`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.palette.primary.main};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  cursor: ${({ disabled }) => disabled ? 'auto' : 'pointer'};
  pointer-events: ${({ disabled }) => disabled ? 'none' : 'auto'};

  &:hover {
    opacity: ${({ disabled }) => (disabled ? 0.5 : 0.8)};
  }
`;

export const ContrastLink = styled.a<{ bold?: boolean }>`
  color: ${({ theme }) => theme.palette.info.light};
  font-weight: ${({ bold }) => (bold ? 700 : 400)};
  text-decoration: none;
`;

export const Contrast = styled.span<{ bold?: boolean; disabled?: boolean, isFullResolved?: boolean }>`
  color: ${({ theme, disabled }) => disabled ? theme.palette.grey[700] : theme.palette.info.light};
  align-items: inherit;
  display: inherit;
  gap: inherit;
  justify-content: center;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  font-weight: ${({ bold }) => (bold ? 600 : 400)};
  text-decoration: ${({ isFullResolved }) => isFullResolved ? 'line-through' : 'none'}
`;

export const Flag = styled.span<{ color?: string }>`
  font-weight: 800;
  color: ${({ color }) => color};
`;

export const SubjectTitle = styled(Typography) <{ display?: string }>`
  font-weight: 500;
  display: ${ ({ display }) => display || 'flex'};
  display: ${({ display }) => display || 'flex'};
  gap: 10px;
  margin: 0px;
`;


SubjectTitle.defaultProps = {
  variant: 'h4',
};

export const NameRow = styled.div`
  display: flex;
  min-width: 95px;
  align-items: center;
  white-space: nowrap;
  justify-content: center;
  border-bottom: 0.1px solid ${colorTheme.palette.common.white};
  margin-bottom: 8px;
  padding-bottom: 8px;
  padding-top: 5px;

  ${Contrast} {
    font-size: 13px;
    cursor: pointer;
    width: 135px;
    position: absolute;
    right: 0;
    width: 135px;
    top: 95px;
  }
`;

export const SubSections = styled.div<{ align?: string }>`
  display: flex;
  flex: 1;
  align-items: inherit;
`;

export const SubSection = styled.div<{ width: string }>`
  flex: 1;
  min-width: ${({ width }) => width};
`;

export const ContactDetailsSection = styled.div`
  height: 100%;
  padding-right: 5px;
  border-right: 0.1px solid ${colorTheme.palette.common.white};
`;

export const ContactDetailsSectionWrapper = styled.div<{ flex?: string }>`
  height: 100%;
  padding: 5px 0 0px 8px;
  margin-right: 15px;
  position: relative;
  flex: ${({ flex }) => flex};

  p {
    margin: 0;
    line-height: 18px;
  }

  &:last-child {
    ${ContactDetailsSection} {
      border: none;
    }
  }
`;

export const DataBoxTitle = styled.p`
  font-size: 14px;
  font-weight: 700;
`;

export const InfoBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  border-bottom: 1px solid ${colorTheme.palette.secondary.main};
`;

export const LeftTitle = styled.div`
  width: 20%;
  min-width: 20%;
  font-size: 16px;
  font-weight: 700;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

export const DataBox = ({ label, value }: ConfigDataType) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      marginRight={2}
    >
      <DataBoxTitle>{label}</DataBoxTitle>

      <Typography>{value === null ? 'N/A' : (value ? 'YES' : 'NO') }</Typography>
    </Box>
  );
};

export const BoxWrapper = styled(Box)({
  border: `1px solid ${colorTheme.palette.secondary.main}`,
  height: '660px',
  padding: '10px 10px 0 10px',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
});

export const ArrowUpwardIcon = styled(ArrowUpwardSharp)<{ disable?: boolean }>`
  font-size: 36px;
  fill: ${colorTheme.palette.common.white};
  background: ${({ disable }) => (!disable ? colorTheme.palette.primary.light : colorTheme.palette.secondary.main)};
  padding: 5px;
  border-radius: 8px;
  cursor: pointer;
`;

export const Dateinfo = styled(Typography)`
  display: flex;
  justify-content: center;
  padding-bottom: 10px;
`;

export const FancyWrapper = styled.div`
  background: ${colorTheme.palette.secondary.main};
`;

export const PersonalInfoContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px;
`;

export const AddressDate = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0px 10px 5px 10px;
  border-bottom: 20px solid ${colorTheme.palette.common.white};
`;

export const ProfileReportContentWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  width: 98%;
  div.bold {
    font-weight: bold;
    &:first-letter {
      text-transform: capitalize;
    }
  }
  div.summary-value {
    width: 20%;
  }
`;

export const HeaderBlock = styled(Typography)`
  color: ${colorTheme.palette.primary.main};
  display: flex;
  height: 49px;
  align-items: center;
  font-weight: 600;
  font-size: 13px;
  padding: 0 10px 3px;
  gap: 10px;
  margin-bottom: 0;
`;

HeaderBlock.defaultProps = {
  variant: 'h3',
};

export const AddButton = styled(Button)`
  width: 100px;
  margin-right: 50px;
  margin-top: 15px;
  color: ${colorTheme.palette.primary.main};
`;
